import React, { useState, useRef, useEffect } from 'react';
import { Skeleton } from './Skeleton';
import { Button } from './Button';
import { Trash } from 'phosphor-react';

interface ProductTileProps {
  name: string;
  sku: string;
  quantity: number;
  unit: string;
  unitPrice?: string;
  total?: string;
  onEdit?: () => void;
  variant?: 'default' | 'report';
  onProductClick?: (e: React.MouseEvent<HTMLElement>) => void;
  onQuantityClick?: (e: React.MouseEvent<HTMLElement>) => void;
  onTileClick?: (e: React.MouseEvent<HTMLElement>) => void;
  isEditable?: boolean;
  isUpdating?: boolean;
  isEditingQuantity?: boolean;
  onQuantitySave?: (quantity: number) => void;
  onQuantityCancel?: () => void;
  minQuantity?: number;
  onDelete?: (e: React.MouseEvent<HTMLElement>) => void;
  showDeleteButton?: boolean;
}

export const ProductTile: React.FC<ProductTileProps> = ({
  name,
  sku,
  quantity,
  unit,
  unitPrice,
  total,
  onEdit,
  variant = 'default',
  onProductClick,
  onQuantityClick,
  onTileClick,
  isEditable = false,
  isUpdating = false,
  isEditingQuantity = false,
  onQuantitySave,
  onQuantityCancel,
  minQuantity = 1,
  onDelete,
  showDeleteButton = false
}) => {
  const isReport = variant === 'report';
  const quantityInputRef = useRef<HTMLInputElement>(null);
  const [quantityValue, setQuantityValue] = useState(quantity.toString());

  const unitLabel = unit === 'unidad' || unit === 'Unidades' || unit === 'unidades' ? 'un' : unit;

  useEffect(() => {
    if (isEditingQuantity && quantityInputRef.current) {
      quantityInputRef.current.focus();
      quantityInputRef.current.select();
    }
  }, [isEditingQuantity]);

  useEffect(() => {
    setQuantityValue(quantity.toString());
  }, [quantity]);

  const handleQuantityBlur = () => {
    const newQuantity = parseInt(quantityValue) || minQuantity;
    if (newQuantity !== quantity && onQuantitySave) {
      onQuantitySave(Math.max(newQuantity, minQuantity));
    } else if (onQuantityCancel) {
      onQuantityCancel();
    }
  };

  const handleQuantityKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const newQuantity = parseInt(quantityValue) || minQuantity;
      if (onQuantitySave) {
        onQuantitySave(Math.max(newQuantity, minQuantity));
      }
    } else if (e.key === 'Escape') {
      setQuantityValue(quantity.toString());
      if (onQuantityCancel) {
        onQuantityCancel();
      }
    }
  };

  return (
    <div className={`border-b border-border w-full max-w-full min-w-0 relative ${
      isEditable && onTileClick ? 'px-0' : 'px-0'
    }`}>
      {/* Mobile: Vertical Layout */}
      <div 
        className={`flex gap-5 items-start w-full md:hidden ${
          isEditable && onTileClick ? 'cursor-pointer hover:bg-muted/50 transition-colors px-2 py-4' : ''
        }`}
        onClick={isEditable && onTileClick ? onTileClick : undefined}
      >
        <div className="flex-1 flex flex-col gap-2 items-start">
          <div className="flex flex-col font-sans font-medium items-start leading-[1.4] w-full">
            {isUpdating ? (
              <>
                <Skeleton width="150px" height="20px" />
                <Skeleton width="100px" height="16px" className="mt-1" />
              </>
            ) : (
              <>
                <p className="text-base text-foreground tracking-[-0.16px] w-full">
                  {name}
                </p>
                <p className="text-xs text-muted-foreground tracking-[-0.12px] w-full">
                  {sku}
                </p>
              </>
            )}
          </div>
          {!isReport && unitPrice && (
            <div className="flex font-sans font-medium gap-1 items-start text-sm text-muted-foreground whitespace-nowrap tracking-[-0.14px] w-full">
              {isEditingQuantity ? (
                <input
                  ref={quantityInputRef}
                  type="number"
                  min={minQuantity}
                  value={quantityValue}
                  onChange={(e) => setQuantityValue(e.target.value)}
                  onBlur={handleQuantityBlur}
                  onKeyDown={handleQuantityKeyDown}
                  className="text-sm text-muted-foreground font-sans font-medium border border-border rounded px-2 py-1 w-20"
                />
              ) : (
                <div className="flex gap-1.5 items-center">
                  {isUpdating ? (
                    <Skeleton width="40px" height="18px" />
                  ) : (
                    <>
                      <p>{quantity}</p>
                      <p>{unitLabel}</p>
                    </>
)}
                </div>
              )}
              <p>x</p>
              {isUpdating ? (
                <Skeleton width="60px" height="18px" />
              ) : (
                <p>{unitPrice} cu</p>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1 items-end" onClick={(e) => e.stopPropagation()}>
          {isReport ? (
            <p className="font-sans font-medium text-base text-foreground text-right tracking-[-0.16px]">
              {quantity} {unitLabel}
            </p>
          ) : (
            <>
              {total && (
                <p className="font-sans font-medium text-base text-foreground text-right tracking-[-0.16px] w-[80px]">
                  {isUpdating ? <Skeleton width="80px" height="20px" /> : total}
                </p>
              )}
              {/* Botón eliminar en mobile - debajo del monto */}
              {isEditable && showDeleteButton && onDelete && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onDelete}
                  aria-label="Eliminar producto"
                >
                  <Trash size={18} weight="bold" />
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Desktop: Horizontal Layout with flexible widths */}
      <div 
        className={`hidden md:flex items-start w-full max-w-full min-w-0 justify-stretch ${
          isEditable && onTileClick ? 'cursor-pointer hover:bg-muted/50 transition-colors rounded-lg p-2 -m-2' : ''
        }`}
        style={{ gap: '16px' }}
        onClick={isEditable && onTileClick ? onTileClick : undefined}
      >
        {/* Product Name and SKU - Flexible width with min-width, allows 2 lines */}
        <div className="basis-[180px] flex-1">
          {isUpdating ? (
            <>
              <Skeleton width="150px" height="20px" />
              <Skeleton width="100px" height="16px" className="mt-1" />
            </>
          ) : (
            <>
              <p className="text-base text-foreground tracking-[-0.16px] font-sans font-medium line-clamp-2">
                {name}
              </p>
              <p className="text-xs text-muted-foreground tracking-[-0.12px] font-sans font-medium truncate mt-0.5">
                {sku}
              </p>
            </>
          )}
        </div>
        
        {isReport ? (
          /* Report variant: Quantity as main data */
          <div className="flex items-start justify-stretch pt-0.5 flex-1">
            <p className="text-base text-foreground tracking-[-0.16px] text-right font-sans font-medium">
              {quantity} {unitLabel}
            </p>
          </div>
        ) : (
          <>
        {/* Quantity - Fixed width to match header */}
        <div className="flex items-start pt-0.5 justify-stretch flex-1">
          {isEditingQuantity ? (
            <input
              ref={quantityInputRef}
              type="number"
              min={minQuantity}
              value={quantityValue}
              onChange={(e) => setQuantityValue(e.target.value)}
              onBlur={handleQuantityBlur}
              onKeyDown={handleQuantityKeyDown}
              className="text-sm text-muted-foreground font-sans font-medium text-right border border-border rounded px-2 py-1 w-20 ml-auto"
            />
          ) : (
            <div className="flex items-start justify-stretch flex-1">
              {isUpdating ? (
                <Skeleton width="60px" height="18px" className="ml-auto" />
              ) : (
                <p className="text-sm text-muted-foreground tracking-[-0.14px] font-sans text-right font-medium ml-auto">
                  {quantity} {unitLabel}
                </p>
              )}
            </div>
          )}
        </div>
        
        {/* Unit Price - Fixed width to match header, Right Aligned */}
        {unitPrice && (
          <div className="flex items-start justify-stretch pt-0.5 text-right flex-1">
            {isUpdating ? (
              <Skeleton width="70px" height="18px" className="ml-auto" />
            ) : (
              <p className="text-sm text-muted-foreground tracking-[-0.14px] font-sans font-medium text-right ml-auto">
                {unitPrice} cu
              </p>
            )}
          </div>
        )}
        
        {/* Total - Flexible width to match header, Right Aligned */}
        {total && (
          <div className="flex items-start justify-end pt-0.5 text-right flex-1">
            {isUpdating ? (
              <Skeleton width="80px" height="20px" />
            ) : (
              <p className="text-base text-foreground tracking-[-0.16px] font-sans font-medium text-right">
                {total}
              </p>
            )}
          </div>
        )}
        
            {/* Edit Button - Hidden */}
            {/* {onEdit && (
        <div className="flex-1 flex justify-end">
          <GhostButton label="Editar" onClick={onEdit} />
        </div>
            )} */}
          </>
        )}
      </div>
    </div>
  );
};

export default ProductTile;

